import React, { useEffect, useRef, useState } from 'react';
import { Text, Icon, Badge, Spinner } from '../ui/index.jsx';

/**
 * Message component for individual chat messages with Bubble design
 */
function ChatMessage({ message }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isUser = message.role === 'user';
  const isSystem = ['system', 'log', 'error', 'success'].includes(message.role);

  // Collapse system logs by default unless they are errors
  if (isSystem && message.role !== 'error' && !isExpanded) {
    // Small system inline message
    return (
      <div className="flex justify-center my-2 animate-fade-in">
        <div
          className={`text-[10px] px-3 py-1 rounded-full border flex items-center gap-2 cursor-pointer transition-all hover:bg-white/5
                  ${message.role === 'error' ? 'text-red-400 border-red-500/20 bg-red-500/5' :
              message.role === 'success' ? 'text-green-400 border-green-500/20 bg-green-500/5' :
                'text-gray-500 border-white/5 bg-white/5'}`}
          onClick={() => setIsExpanded(true)}
        >
          <Icon name={
            message.role === 'error' ? 'alert-circle' :
              message.role === 'success' ? 'check' :
                'terminal'
          } size="xs" />
          <span className="truncate max-w-[200px]">{message.content}</span>
        </div>
      </div>
    );
  }

  const renderIcon = () => {
    switch (message.role) {
      case 'ai': return <Icon name="cpu" size="xs" className="text-[#06B6D4]" />;
      case 'file': return <Icon name="file-plus" size="xs" className="text-emerald-400" />;
      case 'fix': return <Icon name="wrench" size="xs" className="text-amber-400" />;
      case 'error': return <Icon name="alert-triangle" size="xs" className="text-red-400" />;
      default: return <Icon name="message-circle" size="xs" className="text-gray-400" />;
    }
  };

  return (
    <div className={`flex w-full mb-4 animate-fade-in ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <div className="w-6 h-6 rounded-lg bg-[#06B6D4]/10 border border-[#06B6D4]/20 flex items-center justify-center mr-2 mt-1 shrink-0">
          {renderIcon()}
        </div>
      )}

      <div className={`
          relative max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed
          ${isUser
          ? 'bg-gradient-to-br from-[#06B6D4] to-[#7B61FF] text-white shadow-lg shadow-purple-500/20 rounded-tr-sm'
          : 'bg-[#1A1A1A] border border-white/10 text-gray-200 rounded-tl-sm shadow-sm'}
       `}>
        {/* Header for non-user messages */}
        {!isUser && (
          <div className="flex items-center gap-2 mb-1.5 opacity-60 text-[10px] uppercase tracking-wider font-medium">
            <span>Architect AI</span>
            <span className="text-gray-500">•</span>
            <span>{new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        )}

        {/* Content */}
        <div className="whitespace-pre-wrap break-words">
          {message.content}
        </div>

        {/* Special Message Types Attachments */}
        {message.file && (
          <div className={`mt-3 p-2 rounded-lg text-xs font-mono flex items-center gap-2 ${isUser ? 'bg-black/20' : 'bg-black/40 border border-white/5'}`}>
            <Icon name="file" size="xs" />
            <span className={isUser ? 'text-white/80' : 'text-[#7B61FF]'}>{message.file.path}</span>
          </div>
        )}

        {message.previewUrl && (
          <div className="mt-3">
            <a
              href={message.previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                    ${isUser
                  ? 'bg-white/20 hover:bg-white/30 text-white'
                  : 'bg-[#06B6D4]/10 text-[#06B6D4] hover:bg-[#06B6D4]/20 border border-[#06B6D4]/20'}
                `}
            >
              <Icon name="external-link" size="xs" />
              Open Check
            </a>
          </div>
        )}

        {message.error && (
          <div className="mt-3 pt-3 border-t border-white/10">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs text-red-300 flex items-center gap-1 hover:text-red-200"
            >
              <Icon name={isExpanded ? 'chevron-down' : 'chevron-right'} size="xs" />
              Error Details
            </button>
            {isExpanded && (
              <pre className="mt-2 p-2 bg-black/50 rounded text-[10px] text-red-300 overflow-x-auto">
                {typeof message.error === 'string' ? message.error : JSON.stringify(message.error, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>

      {isUser && (
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-gray-700 to-gray-800 ml-2 mt-1 shrink-0 flex items-center justify-center border border-white/10">
          <Icon name="user" size="xs" className="text-gray-400" />
        </div>
      )}
    </div>
  );
}

/**
 * DeployChat component - Consumes messages and renders the list
 */
export default function DeployChat({ messages = [], isLoading = false }) {
  const scrollRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Auto-scroll logic
  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      // Use smooth scroll
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages, autoScroll, isLoading]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
    setAutoScroll(isAtBottom);
  };

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto px-4 py-4 space-y-2 scroll-smooth"
    >
      {messages.map((message) => (
        <ChatMessage key={message.id} message={message} />
      ))}

      {isLoading && (
        <div className="flex w-full justify-start animate-fade-in">
          <div className="w-6 h-6 rounded-lg bg-[#06B6D4]/10 border border-[#06B6D4]/20 flex items-center justify-center mr-2 mt-1 shrink-0">
            <Icon name="cpu" size="xs" className="text-[#06B6D4]" />
          </div>
          <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-2">
            <div className="flex gap-1">
              <div className="w-1.5 h-1.5 bg-[#06B6D4] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-1.5 h-1.5 bg-[#06B6D4] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-1.5 h-1.5 bg-[#06B6D4] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span className="text-xs text-gray-500 font-medium">Thinking...</span>
          </div>
        </div>
      )}

      {/* spacer */}
      <div className="h-4" />
    </div>
  );
}
